import type { ObjectLike } from '@skedulo/horizon-types'
import {
  grpc,
  Query,
  DevToolsRpcMiddleware,
  ErrorMiddleware,
} from '@skedulo/protobuf-web'
import { CacheFactory } from './ui/services/cache'
import type {
  IApiClientWithSessionOptions,
  ICreateObjectRequest,
  ICreateObjectResponse,
  IDeleteObjectRequest,
  IDeleteObjectResponse,
  IGetDataSchemaRequest,
  IGetDataSchemaResponse,
  IGetObjectRequest,
  IGetObjectResponse,
  IGetObjectsRequest,
  IGetObjectsResponse,
  IUpdateObjectRequest,
  IUpdateObjectResponse,
  ISearchRequest,
  ISearchResponse,
} from '../types'

const isParsedObject = (result: unknown): result is ObjectLike =>
  !!(result && Object.keys(result as ObjectLike)?.length)

const isParsedArray = (results: unknown): results is ObjectLike[] =>
  Array.isArray(results)

function assertIsParsedObject(result: unknown): asserts result is ObjectLike {
  if (!isParsedObject(result)) throw Error('Invalid getObject response')
}

function assertIsParsedArray(
  results: unknown,
): asserts results is ObjectLike[] {
  if (!isParsedArray(results)) throw new Error('Invalid getObjects response')
}

const DATA_SCHEMA_CACHE_NAME = 'DataSchema'

export const DataSchemaCache = CacheFactory.create<
  IGetDataSchemaRequest,
  IGetDataSchemaResponse
>(DATA_SCHEMA_CACHE_NAME)

export class QueryApiClientV1 {
  readonly client: Query.API.Client
  private readonly metadata: grpc.Metadata

  constructor({ platformApiDomain, metadata }: IApiClientWithSessionOptions) {
    this.client = new Query.API.Client(
      new ErrorMiddleware(
        new DevToolsRpcMiddleware(
          new Query.API.RPC(platformApiDomain, { metadata }),
        ),
      ),
    )
    this.metadata = metadata
  }

  async createObject(
    request: ICreateObjectRequest,
  ): Promise<ICreateObjectResponse> {
    return this.client.createObject(request, this.metadata)
  }

  async updateObject(
    request: IUpdateObjectRequest,
  ): Promise<IUpdateObjectResponse> {
    return this.client.updateObject(request, this.metadata)
  }

  async deleteObject(
    request: IDeleteObjectRequest,
  ): Promise<IDeleteObjectResponse> {
    return this.client.deleteObject(request, this.metadata)
  }

  async getObject(request: IGetObjectRequest): Promise<IGetObjectResponse> {
    const { result, ...rest } = await this.client.getObject(
      request,
      this.metadata,
    )

    const parsedResult = JSON.parse(result)

    assertIsParsedObject(parsedResult)

    return { result: parsedResult, ...rest }
  }

  async getObjects(request: IGetObjectsRequest): Promise<IGetObjectsResponse> {
    const { results, ...rest } = await this.client.getObjects(
      request,
      this.metadata,
    )

    const parsedResults = JSON.parse(results)

    assertIsParsedArray(parsedResults)

    return { results: parsedResults, ...rest }
  }

  async search(request: ISearchRequest): Promise<ISearchResponse> {
    const { results, ...rest } = await this.client.searchObjects(
      request,
      this.metadata,
    )

    const parsedResults = JSON.parse(results)

    assertIsParsedArray(parsedResults)

    return { results: parsedResults, ...rest }
  }

  async getDataSchema(
    request: IGetDataSchemaRequest,
  ): Promise<IGetDataSchemaResponse> {
    return DataSchemaCache.execute(request, () =>
      this.client.getDataSchema(request, this.metadata),
    )
  }
}
